import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { FaUser, FaGraduationCap, FaBriefcase, FaPhone, FaEnvelope, FaPaperPlane, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import TopNavbar from "../components/Nav/TopNavbar";
import Footer from "../components/Sections/Footer";
import AnimateOnScroll from "../components/AnimateOnScroll";
import { submitTutorApplication } from "../services/tutorApi";

export default function TutorApplication() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    current_year: '',
    academic_field: '',
    department_of_choice: '',
    teaching_experience: '',
    why_choose_you: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear submit error when user starts typing
    if (submitError) {
      setSubmitError(null);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'Full name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^\+?[\d\s-()]+$/.test(formData.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
      if (!formData.current_year.trim()) newErrors.current_year = 'Current academic year is required';
    } else if (step === 2) {
      if (!formData.academic_field.trim()) newErrors.academic_field = 'Academic field is required';
      if (!formData.department_of_choice.trim()) newErrors.department_of_choice = 'Department of choice is required';
      if (!formData.teaching_experience.trim()) newErrors.teaching_experience = 'Teaching experience is required';
      if (!formData.why_choose_you.trim()) newErrors.why_choose_you = 'This field is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForm = () => {
    const step1Valid = validateStep(1);
    const step2Valid = validateStep(2);
    return step1Valid && step2Valid;
  };

  const handleNext = () => {
    if (validateStep(1)) {
      setCurrentStep(2);
      // Scroll to top of form
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    setCurrentStep(1);
    // Scroll to top of form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    setErrors({});
    
    try {
      // Map teaching_experience to experience_at_mia for API compatibility
      const apiData = {
        ...formData,
        experience_at_mia: formData.teaching_experience
      };
      delete apiData.teaching_experience;
      const result = await submitTutorApplication(apiData);
      
      if (result.success) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          current_year: '',
          academic_field: '',
          department_of_choice: '',
          teaching_experience: '',
          why_choose_you: ''
        });
        
        // Reset success message after 8 seconds
        setTimeout(() => {
          setIsSubmitted(false);
        }, 8000);
      } else {
        // Handle API errors
        setSubmitError(result.message || 'Failed to submit application. Please try again.');
        
        // Set field-specific errors if provided
        if (result.fieldErrors && Object.keys(result.fieldErrors).length > 0) {
          setErrors(result.fieldErrors);
        }
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      setSubmitError('An unexpected error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://afriqcm.com';
  const currentUrl = typeof window !== 'undefined' ? window.location.href : `${siteUrl}/tutor-apply`;

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>Tutor Application | Join AFRIQ Bamenda as a Tutor</title>
        <meta name="description" content="Join AFRIQ Bamenda as a tutor. Share your expertise and help shape the future of education in Cameroon. Apply now to become part of our dedicated team." />
        <meta name="keywords" content="AFRIQ tutor application, teaching jobs Cameroon, tutor positions, education careers, academic teaching, Bamenda tutors" />
        <link rel="canonical" href={currentUrl} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content="Tutor Application | Join AFRIQ Bamenda" />
        <meta property="og:description" content="Join AFRIQ Bamenda as a tutor and help shape the future of education in Cameroon." />
        <meta property="og:image" content={`${siteUrl}/logo512.png`} />
        
        {/* Structured Data - JobPosting Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "JobPosting",
            "title": "Tutor at AFRIQ Bamenda",
            "description": "Join AFRIQ Bamenda as a tutor. Share your expertise and help shape the future of education in Cameroon.",
            "identifier": {
              "@type": "PropertyValue",
              "name": "AFRIQ Bamenda",
              "value": "TUTOR-001"
            },
            "datePosted": new Date().toISOString(),
            "employmentType": "PART_TIME",
            "hiringOrganization": {
              "@type": "Organization",
              "name": "AFRIQ Bamenda",
              "sameAs": siteUrl
            },
            "jobLocation": {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Bamenda",
                "addressRegion": "Northwest Region",
                "addressCountry": "CM"
              }
            }
          })}
        </script>
      </Helmet>
      <TopNavbar />
      <Wrapper>
        <div className="container">
          <AnimateOnScroll animation="fadeUp" delay={0} duration={800}>
            <HeaderSection>
              <h1 className="font40 extraBold">Join AFRIQ Bamenda as a Tutor</h1>
              <p className="font16">
                Share your expertise and help shape the future of education in Cameroon. 
                Join our team of dedicated professionals and make a difference in students' lives.
              </p>
            </HeaderSection>
          </AnimateOnScroll>

          <ApplicationForm>
            <AnimateOnScroll animation="scaleUp" delay={200} duration={800}>
              <FormCard>
                <FormHeader>
                  <h2 className="font25 extraBold">Tutor Application Form</h2>
                  <p className="font14">Please provide accurate information about your qualifications and experience</p>
                </FormHeader>

              {isSubmitted ? (
                <SuccessMessage>
                  <FaCheckCircle />
                  <h3 className="font20 extraBold">Application Submitted Successfully!</h3>
                  <p className="font14">Thank you for applying to become a tutor at AFRIQ Bamenda. We'll review your application and contact you within 48 hours.</p>
                </SuccessMessage>
              ) : (
                <Form onSubmit={handleSubmit}>
                  {submitError && (
                    <ErrorMessageBox>
                      <FaExclamationTriangle />
                      <span>{submitError}</span>
                    </ErrorMessageBox>
                  )}

                  {/* Step Indicator - Mobile Only */}
                  {isMobile && (
                    <StepIndicator>
                      <StepItem active={currentStep === 1} completed={currentStep > 1}>
                        <StepNumber active={currentStep === 1} completed={currentStep > 1}>
                          {currentStep > 1 ? '✓' : '1'}
                        </StepNumber>
                        <StepLabel active={currentStep === 1}>Personal Info</StepLabel>
                      </StepItem>
                      <StepLine />
                      <StepItem active={currentStep === 2} completed={false}>
                        <StepNumber active={currentStep === 2} completed={false}>2</StepNumber>
                        <StepLabel active={currentStep === 2}>Experience</StepLabel>
                      </StepItem>
                    </StepIndicator>
                  )}

                  {/* Step 1: Personal Information */}
                  <StepContainer show={!isMobile || currentStep === 1}>
                    <FormGroup>
                      <Label htmlFor="name" className="font14 semiBold">
                        <FaUser />
                        Full Name *
                      </Label>
                      <Input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={errors.name ? 'error' : ''}
                        placeholder="Enter your full name"
                      />
                      {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
                    </FormGroup>

                    <FormRow>
                      <FormGroup>
                        <Label htmlFor="email" className="font14 semiBold">
                          <FaEnvelope />
                          Email Address *
                        </Label>
                        <Input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={errors.email ? 'error' : ''}
                          placeholder="Enter your email address"
                        />
                        {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor="phone" className="font14 semiBold">
                          <FaPhone />
                          Phone Number *
                        </Label>
                        <Input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={errors.phone ? 'error' : ''}
                          placeholder="Enter your phone number"
                        />
                        {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
                      </FormGroup>
                    </FormRow>

                    <FormGroup>
                      <Label htmlFor="current_year" className="font14 semiBold">
                        <FaGraduationCap />
                        Current Academic Year *
                      </Label>
                      <Select
                        id="current_year"
                        name="current_year"
                        value={formData.current_year}
                        onChange={handleInputChange}
                        className={errors.current_year ? 'error' : ''}
                      >
                        <option value="">Select your current academic year</option>
                        <option value="Year 1">Year 1</option>
                        <option value="Year 2">Year 2</option>
                        <option value="Year 3">Year 3</option>
                        <option value="Year 4">Year 4</option>
                        <option value="Year 5">Year 5</option>
                        <option value="Year 6">Year 6</option>
                        <option value="Year 7">Year 7</option>
                        <option value="Graduate">Graduate</option>
                      </Select>
                      {errors.current_year && <ErrorMessage>{errors.current_year}</ErrorMessage>}
                    </FormGroup>

                    {/* Next Button - Mobile Only, Step 1 */}
                    {isMobile && currentStep === 1 && (
                      <StepButton type="button" onClick={handleNext}>
                        Next Step →
                      </StepButton>
                    )}
                  </StepContainer>

                  {/* Step 2: Experience */}
                  <StepContainer show={!isMobile || currentStep === 2}>
                    <FormGroup>
                      <Label htmlFor="academic_field" className="font14 semiBold">
                        <FaGraduationCap />
                        Academic Field of Study *
                      </Label>
                      <Select
                        id="academic_field"
                        name="academic_field"
                        value={formData.academic_field}
                        onChange={handleInputChange}
                        className={errors.academic_field ? 'error' : ''}
                      >
                        <option value="">Select your academic field</option>
                        <option value="Medical">Medical</option>
                        <option value="Engineering">Engineering</option>
                        <option value="Other">Other</option>
                      </Select>
                      {errors.academic_field && <ErrorMessage>{errors.academic_field}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="department_of_choice" className="font14 semiBold">
                        <FaBriefcase />
                        Department of Choice *
                      </Label>
                      <Input
                        type="text"
                        id="department_of_choice"
                        name="department_of_choice"
                        value={formData.department_of_choice}
                        onChange={handleInputChange}
                        className={errors.department_of_choice ? 'error' : ''}
                        placeholder="Which department do you want to tutor in?"
                      />
                      {errors.department_of_choice && <ErrorMessage>{errors.department_of_choice}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="teaching_experience" className="font14 semiBold">
                        <FaBriefcase />
                        Teaching Experience *
                      </Label>
                      <Select
                        id="teaching_experience"
                        name="teaching_experience"
                        value={formData.teaching_experience}
                        onChange={handleInputChange}
                        className={errors.teaching_experience ? 'error' : ''}
                      >
                        <option value="">Select your teaching experience</option>
                        <option value="Fresher">Fresher</option>
                        <option value="1 year">1 year</option>
                        <option value="2 years">2 years</option>
                        <option value="3 years">3 years</option>
                        <option value="4 years+">4 years+</option>
                      </Select>
                      {errors.teaching_experience && <ErrorMessage>{errors.teaching_experience}</ErrorMessage>}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="why_choose_you" className="font14 semiBold">
                        <FaBriefcase />
                        Why Should We Choose You? *
                      </Label>
                      <TextArea
                        id="why_choose_you"
                        name="why_choose_you"
                        value={formData.why_choose_you}
                        onChange={handleInputChange}
                        className={errors.why_choose_you ? 'error' : ''}
                        placeholder="Tell us why you would be a great tutor and what makes you unique..."
                        rows="5"
                      />
                      {errors.why_choose_you && <ErrorMessage>{errors.why_choose_you}</ErrorMessage>}
                    </FormGroup>

                    {/* Navigation Buttons - Mobile Only, Step 2 */}
                    {isMobile && currentStep === 2 && (
                      <StepButtons>
                        <StepButton type="button" onClick={handlePrevious} secondary>
                          ← Previous
                        </StepButton>
                        <SubmitButton type="submit" disabled={isSubmitting} $inStepButtons>
                          {isSubmitting ? (
                            <>
                              <Spinner />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <FaPaperPlane />
                              Submit Application
                            </>
                          )}
                        </SubmitButton>
                      </StepButtons>
                    )}

                    {/* Submit Button - Desktop (always visible) */}
                    {!isMobile && (
                      <SubmitButton type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>
                            <Spinner />
                            Submitting Application...
                          </>
                        ) : (
                          <>
                            <FaPaperPlane />
                            Submit Application
                          </>
                        )}
                      </SubmitButton>
                    )}
                  </StepContainer>
                </Form>
              )}
            </FormCard>
            </AnimateOnScroll>
          </ApplicationForm>

          <InfoSection>
            <InfoCard>
              <h3 className="font20 extraBold">Why Join AFRIQ as a Tutor?</h3>
              <BenefitsList>
                <BenefitItem>
                  <h4 className="font16 semiBold">Competitive Compensation</h4>
                  <p className="font14">Attractive salary packages and performance bonuses</p>
                </BenefitItem>
                <BenefitItem>
                  <h4 className="font16 semiBold">Professional Development</h4>
                  <p className="font14">Continuous training and skill enhancement opportunities</p>
                </BenefitItem>
                <BenefitItem>
                  <h4 className="font16 semiBold">Flexible Schedule</h4>
                  <p className="font14">Work hours that fit your lifestyle and commitments</p>
                </BenefitItem>
                <BenefitItem>
                  <h4 className="font16 semiBold">Impact Lives</h4>
                  <p className="font14">Make a real difference in students' academic journeys</p>
                </BenefitItem>
                <BenefitItem>
                  <h4 className="font16 semiBold">Modern Facilities</h4>
                  <p className="font14">Well-equipped classrooms and teaching resources</p>
                </BenefitItem>
                <BenefitItem>
                  <h4 className="font16 semiBold">Supportive Team</h4>
                  <p className="font14">Collaborative environment with experienced educators</p>
                </BenefitItem>
              </BenefitsList>
            </InfoCard>

            <InfoCard>
              <h3 className="font20 extraBold">Requirements</h3>
              <RequirementsList>
                <RequirementItem>
                  <span className="requirement-icon">✓</span>
                  <div>
                    <h4 className="font16 semiBold">Subject Expertise</h4>
                    <p className="font14">Strong knowledge in your area of specialization</p>
                  </div>
                </RequirementItem>
                <RequirementItem>
                  <span className="requirement-icon">✓</span>
                  <div>
                    <h4 className="font16 semiBold">Communication Skills</h4>
                    <p className="font14">Excellent verbal and written communication abilities</p>
                  </div>
                </RequirementItem>
                <RequirementItem>
                  <span className="requirement-icon">✓</span>
                  <div>
                    <h4 className="font16 semiBold">Passion for Teaching</h4>
                    <p className="font14">Genuine enthusiasm for helping students succeed</p>
                  </div>
                </RequirementItem>
              </RequirementsList>
            </InfoCard>
          </InfoSection>
        </div>
      </Wrapper>
      <Footer />
    </>
  );
}

const Wrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
  position: relative;
  padding-top: 80px;
  
  @media (max-width: 768px) {
    padding-top: 60px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 80%, rgba(75, 46, 131, 0.02) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(75, 46, 131, 0.02) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(75, 46, 131, 0.02) 0%, transparent 50%);
    z-index: 1;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  padding: 40px 0 40px;
  position: relative;
  z-index: 2;
  
  h1 {
    margin-bottom: 15px;
    color: #4b2e83;
  }
  
  p {
    color: #333;
    line-height: 1.6;
    max-width: 700px;
    margin: 0 auto;
  }
`;

const ApplicationForm = styled.div`
  max-width: 900px;
  margin: 0 auto 60px;
  position: relative;
  z-index: 2;
`;

const FormCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(75, 46, 131, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
  }
`;

const FormHeader = styled.div`
  margin-bottom: 30px;
  text-align: center;
  
  h2 {
    color: #4b2e83;
    margin-bottom: 8px;
  }
  
  p {
    color: #333;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: #4b2e83;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    color: #4b2e83;
    font-size: 14px;
  }
`;

const Input = styled.input`
  padding: 15px 20px;
  border: 2px solid rgba(75, 46, 131, 0.2);
  border-radius: 12px;
  font-size: 16px;
  font-family: inherit;
  transition: all 0.3s ease;
  background: #ffffff !important;
  color: #333 !important;
  
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-text-fill-color: #333 !important;
    -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
    box-shadow: 0 0 0px 1000px #ffffff inset !important;
    background-color: #ffffff !important;
    color: #333 !important;
  }
  
  &:focus {
    outline: none;
    border-color: #4b2e83;
    box-shadow: 0 0 0 3px rgba(75, 46, 131, 0.1);
    background: #ffffff !important;
    color: #333 !important;
  }
  
  &::placeholder {
    color: #999;
  }
  
  &.error {
    border-color: #ff6b6b;
    background: #ffffff !important;
    color: #333 !important;
  }
`;

const Select = styled.select`
  padding: 15px 20px;
  border: 2px solid rgba(75, 46, 131, 0.2);
  border-radius: 12px;
  font-size: 16px;
  font-family: inherit;
  transition: all 0.3s ease;
  background: #ffffff !important;
  cursor: pointer;
  color: #333 !important;
  
  option {
    background: #ffffff;
    color: #333;
  }
  
  &:focus {
    outline: none;
    border-color: #4b2e83;
    box-shadow: 0 0 0 3px rgba(75, 46, 131, 0.1);
    background: #ffffff !important;
    color: #333 !important;
  }
  
  &.error {
    border-color: #ff6b6b;
    background: #ffffff !important;
    color: #333 !important;
  }
`;

const TextArea = styled.textarea`
  padding: 15px 20px;
  border: 2px solid rgba(75, 46, 131, 0.2);
  border-radius: 12px;
  font-size: 16px;
  font-family: inherit;
  transition: all 0.3s ease;
  background: #ffffff !important;
  resize: vertical;
  min-height: 120px;
  color: #333 !important;
  
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-text-fill-color: #333 !important;
    -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
    box-shadow: 0 0 0px 1000px #ffffff inset !important;
    background-color: #ffffff !important;
    color: #333 !important;
  }
  
  &:focus {
    outline: none;
    border-color: #4b2e83;
    box-shadow: 0 0 0 3px rgba(75, 46, 131, 0.1);
    background: #ffffff !important;
    color: #333 !important;
  }
  
  &::placeholder {
    color: #999;
  }
  
  &.error {
    border-color: #ff6b6b;
    background: #ffffff !important;
    color: #333 !important;
  }
`;

const ErrorMessage = styled.span`
  color: #ff6b6b;
  font-size: 12px;
  font-weight: 500;
  margin-top: 5px;
`;

const ErrorMessageBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 20px;
  background: #fff5f5;
  border: 2px solid #ff6b6b;
  border-radius: 12px;
  margin-bottom: 20px;
  color: #c53030;
  
  svg {
    font-size: 20px;
    flex-shrink: 0;
  }
  
  span {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
  padding: 20px 0;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => {
    if (props.completed) return '#10b981';
    if (props.active) return '#4b2e83';
    return '#e5e7eb';
  }};
  color: ${props => (props.active || props.completed) ? '#ffffff' : '#6b7280'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
  transform: ${props => props.active ? 'scale(1.1)' : 'scale(1)'};
`;

const StepLabel = styled.span`
  font-size: 12px;
  color: ${props => props.active ? '#4b2e83' : '#6b7280'};
  font-weight: ${props => props.active ? '600' : '500'};
  text-align: center;
  transition: all 0.3s ease;
`;

const StepLine = styled.div`
  width: 60px;
  height: 2px;
  background: #e5e7eb;
  margin: 0 10px;
  position: relative;
  top: -20px;
  
  @media (max-width: 480px) {
    width: 40px;
    margin: 0 5px;
  }
`;

const StepContainer = styled.div`
  display: ${props => props.show ? 'flex' : 'none'};
  flex-direction: column;
  gap: 20px;
  
  @media (min-width: 769px) {
    display: flex;
  }
`;

const StepButtons = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
  
  @media (min-width: 769px) {
    display: none;
  }
  
  button {
    flex: 1;
    padding: 15px 20px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.3s ease;
    margin-top: 0 !important;
  }
`;

const StepButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 15px 20px;
  background: ${props => props.secondary ? '#ffffff' : '#4b2e83'};
  color: ${props => props.secondary ? '#4b2e83' : '#ffffff'};
  border: 2px solid ${props => props.secondary ? '#4b2e83' : 'transparent'};
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    background: ${props => props.secondary ? '#f8f8ff' : '#3a2466'};
    box-shadow: 0 8px 25px rgba(75, 46, 131, 0.3);
  }
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px 30px;
  background: #4b2e83;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 20px;
  
  /* When inside StepButtons, make it uniform with StepButton */
  ${props => props.$inStepButtons && `
    padding: 15px 20px;
    margin-top: 0;
    flex: 1;
    gap: 8px;
  `}
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: #3a2466;
    box-shadow: 0 8px 25px rgba(75, 46, 131, 0.3);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  svg {
    font-size: 16px;
    color: #ffffff;
  }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  
  svg {
    font-size: 60px;
    color: #10b981;
    margin-bottom: 20px;
  }
  
  h3 {
    color: #4b2e83;
    margin-bottom: 10px;
  }
  
  p {
    color: #333;
  }
`;

const InfoSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const InfoCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(75, 46, 131, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
  }
  
  h3 {
    color: #4b2e83;
    margin-bottom: 20px;
    text-align: center;
  }
`;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const BenefitItem = styled.div`
  h4 {
    color: #4b2e83;
    margin-bottom: 8px;
  }
  
  p {
    color: #333;
    margin: 0;
    line-height: 1.4;
  }
`;

const RequirementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RequirementItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  
  .requirement-icon {
    width: 25px;
    height: 25px;
    border-radius: 50%;
    background: #4b2e83;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 12px;
    flex-shrink: 0;
  }
  
  h4 {
    color: #4b2e83;
    margin-bottom: 5px;
  }
  
  p {
    color: #333;
    margin: 0;
    line-height: 1.4;
  }
`;
